import {
  DC_Repositories_Base_Enumerations_BusinessUnit,
  DC_Repositories_Base_Enumerations_Label,
  DC_SAPI_ApigeeCases_Models_Response,
  Hems_StartDeviceScheduleRequest,
  RequestModels_Hems_ChargeSettingsOverrideRequestModel,
  RequestModels_Hems_ChargeSettingsRequestModel,
  RequestModels_Hems_LinkToHemsRequestModel,
  RequestModels_Hems_ReLinkDeviceRequestModel,
  RequestModels_Hems_SessionsAggregatePaginationRequestModel,
  RequestModels_Hems_SessionsPaginationRequestModel,
  ResponseModels_Hems_ChargeSettingsOverrideResponseModel,
  ResponseModels_Hems_ChargeSettingsResponseModel,
  ResponseModels_Hems_DeviceStateResponseModel,
  ResponseModels_Hems_EvSessionAggregateResponseModel,
  ResponseModels_Hems_EvSessionResponseModel,
  ResponseModels_Hems_InitiateOnboardDeviceResponseModel,
  ResponseModels_Hems_ReLinkDeviceResponseModel,
} from '@monorepo-types/dc';

import { request } from '../client';
import type { ApiRequestConfig } from '../client/types';

type LinkDevice = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  requestBody: RequestModels_Hems_LinkToHemsRequestModel;
};
/**
 * Link a customer to Hems.
<param name="businessUnit">The businessUnit.</param><param name="label">The label.</param><param name="requestData">The link request model.</param><param name="customerId">The customerId.</param><param name="cancellationToken">The cancellation token.</param>
 * 
 * @returns ResponseModels_Hems_InitiateOnboardDeviceResponseModel Success
 */
export function linkDevice(
  { businessUnit, label, customerId, requestBody }: LinkDevice,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Hems_InitiateOnboardDeviceResponseModel> {
  return request(
    {
      method: 'POST',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/hems/link`,
      body: requestBody,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type ReLinkDevice = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  requestBody: RequestModels_Hems_ReLinkDeviceRequestModel;
};
/**
 * ReLink a customer to Hems.
<param name="businessUnit">The businessUnit.</param><param name="label">The label.</param><param name="requestData">The link request model.</param><param name="customerId">The customerId.</param><param name="cancellationToken">The cancellation token.</param>
 * 
 * @returns ResponseModels_Hems_ReLinkDeviceResponseModel Success
 */
export function reLinkDevice(
  { businessUnit, label, customerId, requestBody }: ReLinkDevice,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Hems_ReLinkDeviceResponseModel> {
  return request(
    {
      method: 'POST',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/hems/relink`,
      body: requestBody,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type OnboardDevice = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  subscriptionId: string;
};
/**
 * Onboard a device in hems
<param name="businessUnit">The businessUnit.</param><param name="label">The label.</param><param name="customerId">The onboard request model.</param><param name="subscriptionId">The subscription id of the subscription to onboard for.</param><param name="cancellationToken">The cancellation token.</param>
 * 
 * @returns void 
 */
export function onboardDevice(
  { businessUnit, label, customerId, subscriptionId }: OnboardDevice,
  requestConfig: ApiRequestConfig = {},
): Promise<void> {
  return request(
    {
      method: 'POST',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/hems/onboard/${subscriptionId}`,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type OnboardDeviceByIdentifier = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  subscriptionId: string;
  addressId: string;
};
/**
 * Onboard a device in hems
<param name="businessUnit">The businessUnit.</param><param name="label">The label.</param><param name="customerId">The onboard request model.</param><param name="subscriptionId">The subscription id of the subscription to onboard for.</param><param name="addressId">Address identifier</param><param name="cancellationToken">The cancellation token.</param>
 * 
 * @returns void 
 */
export function onboardDeviceByIdentifier(
  { businessUnit, label, customerId, subscriptionId, addressId }: OnboardDeviceByIdentifier,
  requestConfig: ApiRequestConfig = {},
): Promise<void> {
  return request(
    {
      method: 'POST',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/address/${addressId}/hems/onboard/${subscriptionId}`,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type OffboardDevice = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
};
/**
 * Offboard a device in Hems
<param name="businessUnit">The businessUnit.</param><param name="label">The label.</param><param name="customerId">Customer id</param>`
<param name="cancellationToken">The cancellation token.</param>
 * 
 * @returns void 
 */
export function offboardDevice(
  { businessUnit, label, customerId }: OffboardDevice,
  requestConfig: ApiRequestConfig = {},
): Promise<void> {
  return request(
    {
      method: 'POST',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/hems/offboard`,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type GetDeviceState = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  deviceId: string;
};
/**
 * Get the status of a customer device
<param name="businessUnit">The businessUnit.</param><param name="label">The label.</param><param name="customerId">The customerId.</param><param name="deviceId">The deviceId.</param><param name="cancellationToken">The cancellation token.</param>
 * 
 * @returns ResponseModels_Hems_DeviceStateResponseModel Success
 */
export function getDeviceState(
  { businessUnit, label, customerId, deviceId }: GetDeviceState,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Hems_DeviceStateResponseModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/hems/status/${deviceId}`,
      errors: { 400: 'Bad Request', 404: 'Not Found' },
    },
    requestConfig,
  );
}

type Chargesettings = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  requestBody: RequestModels_Hems_ChargeSettingsRequestModel;
};
/**
 * Change the charge settings for a specific device.
<param name="businessUnit">The businessUnit.</param><param name="label">The label.</param><param name="customerId">The customerId.</param><param name="requestData">The chargeSettings request model.</param>
 * 
 * @returns ResponseModels_Hems_ChargeSettingsResponseModel Success
 */
export function chargesettings(
  { businessUnit, label, customerId, requestBody }: Chargesettings,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Hems_ChargeSettingsResponseModel> {
  return request(
    {
      method: 'PUT',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/hems/chargesettings`,
      body: requestBody,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type ChargesettingsOverride = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  requestBody: RequestModels_Hems_ChargeSettingsOverrideRequestModel;
};
/**
 * Override the charge settings for a specific device.
<param name="businessUnit">The businessUnit.</param><param name="label">The label.</param><param name="customerId">The customerId.</param><param name="requestData">The chargeSettings request model.</param>
 * 
 * @returns ResponseModels_Hems_ChargeSettingsOverrideResponseModel Success
 */
export function chargesettingsOverride(
  { businessUnit, label, customerId, requestBody }: ChargesettingsOverride,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Hems_ChargeSettingsOverrideResponseModel> {
  return request(
    {
      method: 'PUT',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/hems/chargesettings/override`,
      body: requestBody,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type StartSchedule = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  deviceId: string;
  requestBody: Hems_StartDeviceScheduleRequest;
};
/**
 * Start the charging schedule for the given device
 *
 * @returns DC_SAPI_ApigeeCases_Models_Response Success
 */
export function startSchedule(
  { businessUnit, label, customerId, deviceId, requestBody }: StartSchedule,
  requestConfig: ApiRequestConfig = {},
): Promise<DC_SAPI_ApigeeCases_Models_Response> {
  return request(
    {
      method: 'POST',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/hems/start/${deviceId}`,
      body: requestBody,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type StopSchedule = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  deviceId: string;
};
/**
 * Stop the charging schedule for the given device
 *
 * @returns DC_SAPI_ApigeeCases_Models_Response Success
 */
export function stopSchedule(
  { businessUnit, label, customerId, deviceId }: StopSchedule,
  requestConfig: ApiRequestConfig = {},
): Promise<DC_SAPI_ApigeeCases_Models_Response> {
  return request(
    {
      method: 'POST',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/hems/stop/${deviceId}`,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type ToggleSchedule = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  deviceId: string;
  enabled: boolean;
};
/**
 * Toggle the charging schedule for the given device
 *
 * @returns DC_SAPI_ApigeeCases_Models_Response Success
 */
export function toggleSchedule(
  { businessUnit, label, customerId, deviceId, enabled }: ToggleSchedule,
  requestConfig: ApiRequestConfig = {},
): Promise<DC_SAPI_ApigeeCases_Models_Response> {
  return request(
    {
      method: 'POST',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/hems/toggle/${deviceId}/${enabled}`,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type GetSessions = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  deviceId: string;
  requestBody?: RequestModels_Hems_SessionsPaginationRequestModel;
};
/**
 * Gets the sessions for a specific device
 *
 * @returns ResponseModels_Hems_EvSessionResponseModel Success
 */
export function getSessions(
  { businessUnit, label, customerId, deviceId, requestBody }: GetSessions,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Hems_EvSessionResponseModel> {
  return request(
    {
      method: 'POST',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/hems/sessions/${deviceId}`,
      body: requestBody,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type GetSessionsAggregate = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  deviceId: string;
  requestBody: RequestModels_Hems_SessionsAggregatePaginationRequestModel;
};
/**
 * Gets the sessions aggregate for a specific device
 *
 * @returns ResponseModels_Hems_EvSessionAggregateResponseModel Success
 */
export function getSessionsAggregate(
  { businessUnit, label, customerId, deviceId, requestBody }: GetSessionsAggregate,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Hems_EvSessionAggregateResponseModel> {
  return request(
    {
      method: 'POST',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/hems/sessions/aggregate/${deviceId}`,
      body: requestBody,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}
